/**
 * Answers "is there a server session?" once, before any authenticated work.
 *
 * The stored hint answers for free on a normal navigation; only an unknown
 * state costs one GET /api/user/me. The probe opts out of the client's 401
 * redirect so the router can navigate in-app and keep the destination.
 *
 * Only a 401 means "signed out". Any other failure (network, timeout, a 5xx,
 * a request aborted by navigating away) says nothing about the session, so it
 * is rethrown for the router's error screen and the next call probes again.
 * Treating it as signed out would send a signed-in user to the sign-in screen.
 */
import { ApiError } from '@/api/errors';
import { shellApi } from '@/api/endpoints/shell';
import { clearSessionHint, hasSessionHint, markSessionActive } from './session';

let resolution: Promise<boolean> | null = null;

export const resolveSession = (): Promise<boolean> => {
  if (resolution) return resolution;
  if (hasSessionHint()) {
    resolution = Promise.resolve(true);
    return resolution;
  }
  resolution = shellApi
    .me(undefined, { skipAuthRedirect: true })
    .then(() => {
      markSessionActive();
      return true;
    })
    .catch((error: unknown) => {
      if (error instanceof ApiError && error.status === 401) {
        clearSessionHint();
        return false;
      }
      resolution = null;
      throw error;
    });
  return resolution;
};

/** Re-ask on the next call — used when a session begins or ends. */
export const forgetSessionResolution = (): void => {
  resolution = null;
};

export const beginSession = (): void => {
  markSessionActive();
  forgetSessionResolution();
};

export const endSession = (): void => {
  clearSessionHint();
  forgetSessionResolution();
};
